import {
	formatVentoraBoostTimeRemaining,
	formatVentoraFanSourceI18nKey,
	readVentoraFanBoostActive,
	readVentoraFanOn,
	readVentoraFanSpeedPercent,
} from 'src/components/DevicesSettings/Settings/SettingsG2/VentoraFan/ventora_fan_settings_helpers';
import { capFirstChar, mathEpsilon } from 'src/helpers/utilities';
import { unwrapFunctionStore, format } from 'svelte-i18n';

const formatTranslation = unwrapFunctionStore(format);

export type VentoraFanStatusLabelRow = {
	name: string;
	icon: string;
	value: string;
	iconColor?: string;
};

/** Label list rows for MMotors Ventora (S4PB-0916176901) device status / sidepanel. */
export const buildS4Pb0916176901StatusLabelList = (
	status: unknown,
	settings: unknown,
): VentoraFanStatusLabelRow[] => {
	const statusPayload = status as Record<string, unknown> | null | undefined;
	const settingsPayload = settings as Record<string, unknown> | null | undefined;

	const fanChannel = statusPayload?.['fan:0'] as Record<string, unknown> | undefined;
	if (!fanChannel) {
		return [];
	}

	const formatFanModeValue = (mode: unknown): string => {
		if (mode === undefined || mode === null || mode === '') return '—';
		const normalizedMode = String(mode).trim().toLowerCase();
		if (normalizedMode === 'auto') {
			return formatTranslation('ventora_run_mode_auto', { default: 'Auto' });
		}
		if (normalizedMode === 'manual') {
			return formatTranslation('ventora_run_mode_manual', { default: 'Manual' });
		}
		return capFirstChar(String(mode));
	};

	const labelRows: VentoraFanStatusLabelRow[] = [];
	const isFanOn = readVentoraFanOn(fanChannel);
	const appendLabelRow = (
		nameKey: string,
		nameDefault: string,
		icon: string,
		value: string,
		iconColor?: string,
	) => {
		labelRows.push({
			name: formatTranslation(nameKey, { default: nameDefault }),
			icon,
			value,
			...(iconColor ? { iconColor } : {}),
		});
	};
	appendLabelRow(
		'ventora_status_mode',
		'Mode',
		'fas fa-fan',
		formatFanModeValue(fanChannel.mode),
		isFanOn ? '--color-primary' : undefined,
	);

	const sourceI18nKey = formatVentoraFanSourceI18nKey(fanChannel.source);
	if (sourceI18nKey && String(fanChannel.mode).trim().toLowerCase() === 'auto') {
		const sourceLabel = formatTranslation(sourceI18nKey, {
			default: capFirstChar(String(fanChannel.source)),
		});
		appendLabelRow(
			'ventora_status_auto_mode',
			'Auto mode',
			'fas fa-a',
			formatTranslation('ventora_status_auto_mode_source', {
				values: { sourceLabel: sourceLabel },
				default: `Auto mode: ${sourceLabel}`,
			}),
			'--color-primary',
		);
	}

	if (readVentoraFanBoostActive(fanChannel)) {
		const boostPayload = fanChannel.boost as Record<string, unknown> | undefined;
		const boostTimeRemaining = formatVentoraBoostTimeRemaining(boostPayload);
		const boostDisplayValue = boostTimeRemaining
			? formatTranslation('ventora_status_boost_remaining', {
					values: { boostTimeRemaining: boostTimeRemaining },
					default: `Boost: ${boostTimeRemaining}`,
				})
			: formatTranslation('ventora_boost_tagline_active', { default: 'Boost on' });
		appendLabelRow(
			'ventora_status_boost',
			'Boost',
			'fas fa-bolt',
			boostDisplayValue,
			'--color-primary',
		);
	}

	if (isFanOn) {
		appendLabelRow(
			'ventora_status_speed',
			'Speed',
			'fas fa-tachometer-alt',
			`${readVentoraFanSpeedPercent(fanChannel)}%`,
		);
		if (typeof fanChannel.apower === 'number' && !Number.isNaN(fanChannel.apower)) {
			appendLabelRow(
				'ventora_status_apower',
				'Power',
				'fas fa-bolt',
				`${mathEpsilon(fanChannel.apower)} W`,
			);
		}
	}

	const temperatureChannel = statusPayload?.['temperature:0'] as
		| { tC?: number; tF?: number; errors?: unknown[] }
		| undefined;
	if (
		temperatureChannel &&
		typeof temperatureChannel.tC === 'number' &&
		!Number.isNaN(temperatureChannel.tC) &&
		temperatureChannel.tC > -275 &&
		(!Array.isArray(temperatureChannel.errors) ||
			temperatureChannel.errors.length === 0 ||
			temperatureChannel.errors[0] === 'Sensor driver missing from firmware')
	) {
		const useFahrenheit = settingsPayload?.ht_ui?.temperature_unit === 'F';
		const displayedTemperature =
			useFahrenheit &&
			typeof temperatureChannel.tF === 'number' &&
			!Number.isNaN(temperatureChannel.tF)
				? temperatureChannel.tF
				: temperatureChannel.tC;
		const temperatureUnit = useFahrenheit ? '°F' : '°C';
		appendLabelRow(
			'ventora_status_env_temperature',
			'Temperature',
			'fas fa-thermometer-half',
			`${displayedTemperature.toFixed(1)} ${temperatureUnit}`,
		);
	}
	const humidityChannel = statusPayload?.['humidity:0'] as { rh?: number } | undefined;
	if (typeof humidityChannel?.rh === 'number' && !Number.isNaN(humidityChannel.rh)) {
		appendLabelRow(
			'ventora_status_env_humidity',
			'Humidity',
			'fas fa-droplet',
			`${humidityChannel.rh.toFixed(0)} %`,
		);
	}

	const illuminanceChannel = statusPayload?.['illuminance:0'] as
		| { lux?: number; illumination?: string }
		| undefined;
	if (typeof illuminanceChannel?.lux === 'number' && !Number.isNaN(illuminanceChannel.lux)) {
		const luxDisplay = `${mathEpsilon(illuminanceChannel.lux)} lx`;
		const illuminationState = illuminanceChannel.illumination;
		const illuminanceDisplayValue =
			typeof illuminationState === 'string' && illuminationState !== ''
				? ` ${capFirstChar(
						formatTranslation('alarm_state_' + illuminationState, {
							default: illuminationState,
						}),
					)}`
				: luxDisplay;
		appendLabelRow(
			'ventora_status_env_illuminance',
			'Illuminance',
			'fas fa-sun',
			illuminanceDisplayValue,
		);
	}

	return labelRows;
};
